import type { FirmOverviewModel, FirmOverviewState } from '.';
import { assembleFirm } from './assembleFirm';
import type { AssemblyContext, FirmAssembly } from './assembleFirm';
import { FirmData } from './FirmData';
import { computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { BillingResponse } from '@thinkalpha/locates-ws-client';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import type { FirmMembershipWithProfile } from '@thinkalpha/ui-types/types/admin/firms';
import type { FirmFacts, FirmOverviewSource, FirmRef } from 'src/admin/firms/FirmOverview/source';
import { firmDisplayName } from 'src/admin/firms/FirmOverview/standing';
import type { Provider } from 'src/admin/LAB/contracts/providers';
import { DisposableBoxMap } from 'src/admin/lib/DisposableBoxMap';
import { observeOutcome } from 'src/admin/lib/queryOutcome';
import type { Outcome } from 'src/admin/lib/queryOutcome';
import { isRestricted, restricted } from 'src/admin/lib/visible';
import type { RestrictedReason, Visible } from 'src/admin/lib/visible';
import { AdminViewerScope } from 'src/admin/models/AdminViewerScope/AdminViewerScope';
import type { ViewerStanding } from 'src/admin/models/AdminViewerScope/ViewerStanding';
import { inject, injectable } from 'src/features/ioc';
import { getAllBillingRulesQuery } from 'src/queries/labBilling';
import { getAllProvidersQuery } from 'src/queries/labProviders';
import { getAllRoutesQuery } from 'src/queries/labRoutes';
import type { ReactBindings } from 'src/types/bindings';

const LOADING: FirmOverviewState = { status: 'loading' };

const unready = (assembly: Exclude<FirmAssembly, { status: 'ready' }>): FirmOverviewState =>
    assembly.status === 'failed' ? { status: 'error', error: assembly.error } : LOADING;

/** The focal firm's relations, one level out. */
const relationsOf = (firm: { parents?: FirmRef[]; children?: FirmRef[] }): FirmRef[] => [
    ...(firm.parents ?? []),
    ...(firm.children ?? []),
];

function buildContext(
    standing: ViewerStanding,
    fetched: readonly FirmData[],
    superuserLists: AssemblyContext['superuserLists'],
): AssemblyContext {
    const billingOwnedByFirm = new Map<string, Outcome<BillingResponse>>();
    const membershipsById = new Map<string, FirmMembershipWithProfile>();
    const knownFirmIds = new Set<string>();

    for (const firm of standing.memberFirms) {
        knownFirmIds.add(firm.id);
        for (const ref of relationsOf(firm)) knownFirmIds.add(ref.id);
    }
    for (const model of fetched) {
        const outcomes = model.outcomes.value;
        // A firm that refused the viewer never had its billing requested, so its rules are refused too.
        // Likewise a firm still loading or failed has not had its billing requested.
        billingOwnedByFirm.set(
            model.firmId,
            outcomes.firm.status === 'ok'
                ? outcomes.billing
                : outcomes.firm.status === 'refused'
                  ? { status: 'refused', httpStatus: outcomes.firm.httpStatus }
                  : outcomes.firm,
        );
        if (outcomes.members.status === 'ok') {
            for (const membership of outcomes.members.data) membershipsById.set(membership.id, membership);
        }
        if (outcomes.firm.status === 'ok') {
            knownFirmIds.add(outcomes.firm.data.id);
            for (const ref of relationsOf(outcomes.firm.data)) knownFirmIds.add(ref.id);
        }
    }
    return { standing, superuserLists, billingOwnedByFirm, membershipsById, knownFirmIds };
}

/** Names the viewer may know: their own firms, every firm that opened, and the refs those expose one hop out. */
function firmNames(standing: ViewerStanding, fetched: readonly FirmData[]): Record<string, Visible<string>> {
    const names: Record<string, Visible<string>> = {};
    const add = (ref: FirmRef) => {
        names[ref.id] ??= firmDisplayName(ref);
    };
    for (const firm of standing.memberFirms) {
        add(firm);
        relationsOf(firm).forEach(add);
    }
    for (const model of fetched) {
        const { firm } = model.outcomes.value;
        if (firm.status !== 'ok') continue;
        add(firm.data);
        relationsOf(firm.data).forEach(add);
    }
    for (const model of fetched) {
        if (model.outcomes.value.firm.status === 'refused') names[model.firmId] ??= restricted('other-firm');
    }
    return names;
}

/** Name and owner of every provider the viewer can read; a route target outside that list is restricted. */
function providerRefs(
    readable: Provider[],
    facts: readonly FirmFacts[],
    fallback: RestrictedReason,
): FirmOverviewSource['providerRefs'] {
    const refs: FirmOverviewSource['providerRefs'] = {};
    for (const provider of readable) refs[provider.id] = { name: provider.name, firmId: provider.firmId };
    for (const fact of facts) {
        const routes = [
            ...(isRestricted(fact.routesOwned) ? [] : fact.routesOwned.visible),
            ...(isRestricted(fact.routesGranted) ? [] : fact.routesGranted),
        ];
        for (const target of routes.flatMap((route) => route.targets)) {
            refs[target.providerId] ??= restricted(fallback);
        }
    }
    return refs;
}

@injectable()
export class FirmOverviewModelImpl implements FirmOverviewModel {
    readonly #stack = new DisposableStack();
    readonly #queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    readonly #viewer: AdminViewerScope;

    /** The focal firm's queries. The box owns them; the signal lets `state` follow a change of firm. */
    readonly #focalBox = this.#stack.use(new DisposableBox<FirmData>());
    readonly #focal = signal<FirmData | undefined>(undefined);
    /** One box per related firm, so each firm's observers go away when it stops being related. */
    readonly #related: DisposableBoxMap<FirmData>;

    readonly state: ReadonlySignal<FirmOverviewState>;
    readonly canElevate: ReadonlySignal<boolean>;

    constructor(
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('ActiveUserModel') activeUserModel: ReactBindings['ActiveUserModel'],
    ) {
        this.#queryObserverModelFactory = queryObserverModelFactory;
        this.#related = this.#stack.use(new DisposableBoxMap<FirmData>());
        this.#viewer = this.#stack.use(new AdminViewerScope({ queryObserverModelFactory, activeUserModel }));
        this.canElevate = this.#viewer.canElevate;
        const isSuperuser = this.#viewer.isSuperuser;

        // Superusers answer "granted to" and "applied to" from the unfiltered lists; nobody else may read them.
        const allRoutes = observeOutcome(
            this.#stack,
            queryObserverModelFactory,
            computed(() => ({ ...getAllRoutesQuery(), enabled: isSuperuser.value })),
        );
        const allBilling = observeOutcome(
            this.#stack,
            queryObserverModelFactory,
            computed(() => ({ ...getAllBillingRulesQuery({}), enabled: isSuperuser.value })),
        );
        // `GET /v1/providers` unfiltered: every provider the viewer can read, to name and place route targets.
        const readableProviders = observeOutcome(
            this.#stack,
            queryObserverModelFactory,
            computed(() => getAllProvidersQuery()),
        );

        this.#stack.defer(
            effect(() => {
                const focal = this.#focal.value;
                const firm = focal?.firm.value;
                const relatedIds = new Set<string>();
                if (focal && firm?.status === 'ok') {
                    for (const ref of relationsOf(firm.data)) {
                        if (ref.id !== focal.firmId) relatedIds.add(ref.id);
                    }
                }
                untracked(() =>
                    this.#related.reconcile(relatedIds, (id) => new FirmData(id, queryObserverModelFactory)),
                );
            }),
        );

        this.state = computed<FirmOverviewState>(() => {
            const focal = this.#focal.value;
            if (!focal) return LOADING;

            const viewer = this.#viewer.standing.value;
            if (viewer.status === 'failed') return { status: 'error', error: viewer.error };
            if (viewer.status !== 'ok') return LOADING;
            const standing = viewer.data;

            const related = this.#related.current.value;
            const fetched = [focal, ...related.values()];
            const context = buildContext(
                standing,
                fetched,
                isSuperuser.value ? { routes: allRoutes.value, billing: allBilling.value } : undefined,
            );

            const focalAssembly = assembleFirm(focal.outcomes.value, context);
            if (focalAssembly.status !== 'ready') return unready(focalAssembly);
            const { facts, members } = focalAssembly;
            if (isRestricted(facts)) return { status: 'restricted', reason: facts.reason };

            // Related firms never hold the page: one still loading or broken is marked as such.
            const relatedFacts: Record<string, Visible<FirmFacts>> = {};
            for (const ref of relationsOf(facts.firm)) {
                const model = related.get(ref.id);
                const assembly = model ? assembleFirm(model.outcomes.value, context) : { status: 'pending' as const };
                relatedFacts[ref.id] =
                    assembly.status === 'ready'
                        ? assembly.facts
                        : restricted(assembly.status === 'pending' ? 'loading' : 'unavailable');
            }

            // The provider list names route targets; while it is out, or if it broke, targets say so.
            const providers = readableProviders.value;
            const readable = providers.status === 'ok' ? providers.data : [];
            const targetFallback =
                providers.status === 'pending'
                    ? 'loading'
                    : providers.status === 'failed'
                      ? 'unavailable'
                      : 'other-firm';
            const visibleFacts = [
                facts,
                ...Object.values(relatedFacts).filter((value): value is FirmFacts => !isRestricted(value)),
            ];
            return {
                status: 'ready',
                source: {
                    viewer: standing.context,
                    focal: facts,
                    members,
                    related: relatedFacts,
                    firmNames: firmNames(standing, fetched),
                    providerRefs: providerRefs(readable, visibleFacts, targetFallback),
                },
            };
        });
    }

    setFirmId(firmId: string): void {
        if (this.#focal.peek()?.firmId === firmId) return;
        this.#focalBox.next(() => new FirmData(firmId, this.#queryObserverModelFactory));
        this.#focal.value = this.#focalBox.current;
    }

    [Symbol.dispose](): void {
        this.#stack.dispose();
    }
}
